import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calendar, BarChart3, Plane, Ship, Train, Truck, Globe, TrendingUp, Network, Brain, Anchor, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { translations } from "@/utils/translations";

const Index = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const t = translations[currentLanguage as keyof typeof translations];

  const articles = [
    {
      id: 1,
      title: t.articles[1].title,
      excerpt: t.articles[1].excerpt,
      category: t.articles[1].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1713846047266-12aa96cbbb6c?w=800&auto=format&fit=crop&q=80",
      icon: Plane,
      badgeColor: "bg-[rgb(59,130,246)]",
      stats: t.articles[1].stats
    },
    {
      id: 2,
      title: t.articles[2].title,
      excerpt: t.articles[2].excerpt,
      category: t.articles[2].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1532635042-a6f6ad4745f9?w=800&auto=format&fit=crop&q=80",
      icon: TrendingUp,
      badgeColor: "bg-[rgb(59,130,246)]",
      stats: t.articles[2].stats
    },
    {
      id: 3,
      title: t.articles[3].title,
      excerpt: t.articles[3].excerpt,
      category: t.articles[3].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1628789665182-84e2b3757b59?w=800&auto=format&fit=crop&q=80",
      icon: Ship,
      badgeColor: "bg-[rgb(59,130,246)]",
      stats: t.articles[3].stats
    },
    {
      id: 4,
      title: t.articles[4].title,
      excerpt: t.articles[4].excerpt,
      category: t.articles[4].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1633365636129-0441d2b94d96?w=800&auto=format&fit=crop&q=80",
      icon: Train,
      badgeColor: "bg-[rgb(34,197,94)]",
      stats: t.articles[4].stats
    },
    {
      id: 5,
      title: t.articles[5].title,
      excerpt: t.articles[5].excerpt,
      category: t.articles[5].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1759272548470-d0686d071036?w=800&auto=format&fit=crop&q=80",
      icon: Globe,
      badgeColor: "bg-[rgb(34,197,94)]",
      stats: t.articles[5].stats
    },
    {
      id: 6,
      title: t.articles[6].title,
      excerpt: t.articles[6].excerpt,
      category: t.articles[6].category,
      date: "December 30, 2025",
      image: "https://images.unsplash.com/photo-1622030433415-153f4ada40fe?w=800&auto=format&fit=crop&q=80",
      icon: Truck,
      badgeColor: "bg-[rgb(59,130,246)]",
      stats: t.articles[6].stats
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8 space-y-12">
        {/* Header Section */}
        <section id="industry-highlights">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">{t.weeklyHighlights}</h2>
            </div>
            <LanguageSwitcher 
              currentLanguage={currentLanguage} 
              onLanguageChange={setCurrentLanguage} 
            />
          </div>

          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => {
              const IconComponent = article.icon;
              return (
                <Link key={article.id} to={`/article/${article.id}`}>
                  <Card className="hover:shadow-lg transition-shadow">
                    {/* Article Image */}
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-[192px] object-cover"
                      />
                      <Badge className={`absolute top-4 left-4 ${article.badgeColor} text-white border-transparent`}>
                        <IconComponent className="h-3 w-3 mr-1" />
                        {article.category}
                      </Badge>
                    </div>

                    {/* Article Content */}
                    <CardHeader>
                      <CardTitle className="text-lg">{article.title}</CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-1" />
                        {article.date}
                      </div>
                      <p className="text-gray-600 mb-4">{article.excerpt}</p>
                    </CardHeader>

                    <CardContent>
                      {/* Stats */}
                      <div className="space-y-2 mb-4">
                        {Object.entries(article.stats).map(([key, value], index) => (
                          <div key={index} className="flex justify-between">
                            <span className="text-sm text-gray-600">{key}:</span>
                            <span className={`font-semibold ${
                              index === 0 ? 'text-blue-600' : 
                              index === 1 ? 'text-green-600' : 
                              'text-orange-600'
                            }`}>{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-between items-center">
                        <Badge variant="secondary">{article.category}</Badge>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">{t.readMore}</Button>
                          <Button variant="outline" size="sm" className="ml-auto">
                            <Share2 className="h-4 w-4 mr-2" />
                            {t.share}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        <Separator />

        {/* Footer Section */}
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h3 className="text-xl font-bold mb-2">{t.portalName}</h3>
              <p className="text-blue-200">{t.title}</p>
              <p className="text-blue-200 text-sm">{t.subtitle}</p>
            </div>
            <div className="mt-4 md:mt-0">
              {/* Additional footer content can go here */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;